import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSite } from "../../lib/useSite";
import { Reveal, Eyebrow } from "../../components/bits";
import { Check, Sparkles } from "lucide-react";

export default function Pricing() {
  const site = useSite();
  const { user } = useAuth();
  const nav = useNavigate();
  const packages = site?.packages || [];
  const plans = site?.plans || [];

  // Route a tier CTA into the real account booking journey.
  const startBooking = (tier) => {
    if (user && user.role === "customer") nav(`/portal?book=${encodeURIComponent(tier)}`);
    else if (user && (user.role === "founder_ceo")) nav(`/portal?book=${encodeURIComponent(tier)}`);
    else nav(`/login?next=${encodeURIComponent(`/portal?book=${tier}`)}`);
  };

  const priceLabel = (p) => {
    if (p.type === "bespoke") return "Bespoke";
    if (p.type === "membership") return `From £${p.rate}`;
    return `£${p.rate}`;
  };
  const unitLabel = (p) => (p.type === "hourly" ? "/ hour" : p.type === "membership" ? "/ 4 weeks" : "quotation");

  const cta = (p) => {
    if (p.type === "bespoke") return <Link to="/quote" className="mt-6 text-center py-3 rounded-sm text-sm font-semibold btn-outline-gold" data-testid={`pkg-cta-${p.name.replace(/ /g, "-")}`}>Request Quote</Link>;
    if (p.type === "membership") return <a href="#memberships" className="mt-6 text-center py-3 rounded-sm text-sm font-semibold btn-outline-gold" data-testid="pkg-cta-membership">View memberships</a>;
    return <button onClick={() => startBooking(p.name)} data-testid={`pkg-cta-${p.name.replace(/ /g, "-")}`} className={`mt-6 py-3 rounded-sm text-sm font-semibold ${p.name === "Elite Signature" ? "btn-gold" : "btn-outline-gold"}`}>Get Started</button>;
  };

  return (
    <div>
      <section className="bg-[#0a0a0a] relative">
        <div className="absolute inset-0 noise-overlay" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-16">
          <Eyebrow dark>Pricing</Eyebrow>
          <h1 className="font-display text-[#fdfbf7] text-5xl sm:text-6xl tracking-tight leading-[1.05]">Transparent, tiered pricing</h1>
          <p className="text-[#cfcabf] mt-5 max-w-2xl">
            Choose the standard that suits you. Essential & Premium have a 3-hour minimum; Elite Signature a 4-hour minimum.
            Optional specialist services and add-ons are priced separately and shown clearly before checkout.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {packages.map((p, i) => (
          <Reveal key={p.id || p.name} delay={i * 0.05}>
            <div className={`p-7 h-full flex flex-col rounded-sm border ${p.name === "Elite Signature" ? "bg-[#0a0a0a] border-[#d4af37]" : "bg-white border-[#e8e5de]"}`} data-testid={`package-${p.name.replace(/ /g, "-")}`}>
              <p className={`text-[11px] tracking-luxe uppercase ${p.name === "Elite Signature" ? "text-[#d4af37]" : "text-[#b5952f]"}`}>{p.name}</p>
              <div className="mt-4 flex items-baseline gap-1">
                <span className={`font-display text-4xl ${p.name === "Elite Signature" ? "text-[#fdfbf7]" : "text-[#0a0a0a]"}`}>{priceLabel(p)}</span>
                <span className={`text-xs ${p.name === "Elite Signature" ? "text-[#a3a3a3]" : "text-[#8a8a8a]"}`}>{unitLabel(p)}</span>
              </div>
              <p className={`text-sm mt-4 flex-1 leading-relaxed ${p.name === "Elite Signature" ? "text-[#cfcabf]" : "text-[#555]"}`}>{p.description}</p>
              {p.min_hours > 0 && <p className={`text-xs mt-3 ${p.name === "Elite Signature" ? "text-[#a3a3a3]" : "text-[#8a8a8a]"}`}>Min {p.min_hours} hours</p>}
              {cta(p)}
            </div>
          </Reveal>
        ))}
      </section>

      {/* Memberships */}
      <section id="memberships" className="bg-[#faf9f5] border-y border-[#e8e5de]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <Eyebrow>Cleaning Memberships</Eyebrow>
          <h2 className="font-display text-4xl tracking-tight mt-2">Save with a recurring membership</h2>
          <p className="text-[#555] mt-3 max-w-2xl text-sm">Members enjoy regular cleans at a preferential, predictable monthly price — billed every 4 weeks and cancellable anytime from your account.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {plans.map((p) => (
              <div key={p.id || p.name} className="bg-white border border-[#e8e5de] rounded-sm p-6 flex flex-col" data-testid={`membership-${p.name.replace(/ /g, "-")}`}>
                <p className="text-[11px] tracking-luxe uppercase text-[#b5952f]">{p.tier}</p>
                <h3 className="font-display text-xl mt-1">{p.name}</h3>
                <p className="font-display text-3xl mt-2">£{p.price}<span className="text-sm text-[#8a8a8a]"> / 4 weeks</span></p>
                <p className="text-sm text-[#555] mt-2 flex-1">{p.description}</p>
                <button onClick={() => (user ? nav("/portal") : nav("/login?next=/portal"))} data-testid={`membership-cta-${p.name.replace(/ /g, "-")}`} className="btn-gold py-2.5 rounded-sm text-sm mt-4">Join from your account</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialist teaser */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="bg-[#0a0a0a] rounded-sm p-8 flex flex-col md:flex-row md:items-center justify-between gap-5 noise-overlay">
          <div>
            <div className="flex items-center gap-2 text-[#d4af37]"><Sparkles size={18} /><p className="text-[11px] tracking-luxe uppercase">Specialist Cleaning</p></div>
            <h3 className="font-display text-3xl text-[#fdfbf7] mt-2">Ovens · carpets · mattresses · upholstery · curtains · windows · organisation</h3>
            <p className="text-[#cfcabf] text-sm mt-2 max-w-2xl">Fixed prices where we can, a tailored quote where scope requires it. Add them to any clean or book on their own.</p>
          </div>
          <Link to="/specialist" className="btn-gold px-7 py-3 rounded-sm text-sm whitespace-nowrap" data-testid="pricing-specialist-cta">View specialist prices</Link>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-20">
        <div className="card-luxe p-8">
          <h3 className="font-display text-2xl tracking-tight">What's included with every clean</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
            {["Carefully managed, professional cleaners", "Clear itemised pricing", "Quality-controlled results",
              "Reliable communication", "Flexible scheduling", "Specialist add-ons available"].map((t) => (
              <p key={t} className="flex items-center gap-2 text-sm"><Check size={16} className="text-[#d4af37]" /> {t}</p>
            ))}
          </div>
          <p className="text-xs text-[#8a8a8a] mt-6">
            No discounts are applied without management approval. Diamond Elite Ultra is quoted individually on consultation.
          </p>
        </div>
      </section>
    </div>
  );
}
